import { logger } from "@/lib/logger";
import { useEffect, useRef, useState, useCallback } from "react";
import imageCompression from "browser-image-compression";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Upload, X, FileText, Image, Loader2, ArrowRight, ArrowLeft, Check, Send, Edit2, Home, Building2, Hotel, Briefcase, HelpCircle, Globe, MoreHorizontal, LogIn } from "lucide-react";
import { toast } from "sonner";
import { Progress } from "@/components/ui/progress";
import { supabase } from "@/integrations/supabase/client";
import { motion, AnimatePresence } from "framer-motion";
import { AuthModal } from "@/components/auth/AuthModal";

const IMAGE_COMPRESSION_OPTIONS = {
  maxSizeMB: 1,
  maxWidthOrHeight: 1920,
  useWebWorker: true,
  fileType: "image/jpeg"
};

const NOTIFY_EMAIL = "team@minzo.art";

interface UploadedFile {
  id: string;
  file: File;
  preview?: string;
}

const PLACEHOLDER_EXAMPLES = [
  "I'm looking for warm marble slabs for a kitchen island…",
  "We need minimal black skirting that blends into the wall…",
  "Help me find a stair system with hidden LED lighting…",
  "I want spa-inspired bathroom tiles in soft earthy tones…",
  "We're sourcing large-format porcelain for a hotel lobby…",
  "I'd love textured stone cladding for an outdoor terrace…",
];

function useTypewriter(examples: string[], speed = 55, pause = 2200, del = 25) {
  const [text, setText] = useState("");
  const idx = useRef(0);
  const charIdx = useRef(0);
  const deleting = useRef(false);

  useEffect(() => {
    const tick = () => {
      const current = examples[idx.current];
      if (!deleting.current) {
        charIdx.current++;
        setText(current.slice(0, charIdx.current));
        if (charIdx.current === current.length) { deleting.current = true; return pause; }
        return speed + Math.random() * 30;
      } else {
        charIdx.current--;
        setText(current.slice(0, charIdx.current));
        if (charIdx.current === 0) { deleting.current = false; idx.current = (idx.current + 1) % examples.length; return 500; }
        return del;
      }
    };
    let timeout: ReturnType<typeof setTimeout>;
    const schedule = () => { timeout = setTimeout(schedule, tick()); };
    timeout = setTimeout(schedule, 900);
    return () => clearTimeout(timeout);
  }, [examples, speed, pause, del]);

  return text;
}

interface Answers {
  space_type: string;
  project_location: string;
}

const optionIcons: Record<string, React.ReactNode> = {
  "Villa / Private House": <Home className="h-4 w-4 shrink-0" />,
  "Apartment / Penthouse": <Building2 className="h-4 w-4 shrink-0" />,
  "Resort / Boutique Hotel": <Hotel className="h-4 w-4 shrink-0" />,
  "Commercial / Office": <Briefcase className="h-4 w-4 shrink-0" />,
  "US": <Globe className="h-4 w-4 shrink-0" />,
  "Europe": <Globe className="h-4 w-4 shrink-0" />,
  "Asia": <Globe className="h-4 w-4 shrink-0" />,
  "Middle East": <Globe className="h-4 w-4 shrink-0" />,
  "Other": <MoreHorizontal className="h-4 w-4 shrink-0" />,
};

const questions = [
  { key: "space_type" as keyof Answers, label: "What type of space are you designing for?", options: ["Villa / Private House", "Apartment / Penthouse", "Resort / Boutique Hotel", "Commercial / Office", "Other"] },
  { key: "project_location" as keyof Answers, label: "Where is your project located?", options: ["US", "Europe", "Asia", "Middle East", "Other"] },
];

type Step = "input" | "questions" | "summary" | "submitted";
const ease = [0.25, 0.1, 0.25, 1] as const;

interface ConversationFormProps {
  variant?: "hero" | "default";
}

export function ConversationForm({ variant = "default" }: ConversationFormProps) {
  const isHero = variant === "hero";

  const [step, setStep] = useState<Step>("input");
  const [message, setMessage] = useState("");
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState<Answers>({ space_type: "", project_location: "" });
  const [otherTexts, setOtherTexts] = useState<Partial<Record<keyof Answers, string>>>({});
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ current: number; total: number } | null>(null);
  const [displayUploadPercent, setDisplayUploadPercent] = useState(0);
  const [email, setEmail] = useState("");
  const [pendingSubmissionId, setPendingSubmissionId] = useState<string | null>(null);
  const [pendingConciergeId, setPendingConciergeId] = useState<string | null>(null);
  const [projectCreated, setProjectCreated] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const progressAnimRef = useRef<{ startTs: number; lastCompleted: number } | null>(null);

  const placeholder = useTypewriter(PLACEHOLDER_EXAMPLES);

  // Progress animation
  useEffect(() => {
    if (!isSubmitting || !uploadProgress) { setDisplayUploadPercent(0); progressAnimRef.current = null; return; }
    const total = Math.max(1, uploadProgress.total);
    const base = uploadProgress.current / total * 100;
    setDisplayUploadPercent((prev) => Math.max(prev, base));
    progressAnimRef.current = { startTs: performance.now(), lastCompleted: uploadProgress.current };
    const id = window.setInterval(() => {
      setDisplayUploadPercent((prev) => {
        const p = uploadProgress; if (!p) return 0;
        const t = Math.max(1, p.total); const completed = p.current;
        if (progressAnimRef.current && progressAnimRef.current.lastCompleted !== completed)
          progressAnimRef.current = { startTs: performance.now(), lastCompleted: completed };
        const basePct = completed / t * 100;
        const capPct = completed >= t ? 100 : (completed + 0.9) / t * 100;
        const startTs = progressAnimRef.current?.startTs ?? performance.now();
        const elapsed = performance.now() - startTs;
        const alpha = Math.min(1, elapsed / 1200);
        const eased = 1 - Math.pow(1 - alpha, 3);
        const target = basePct + (capPct - basePct) * eased;
        return Math.min(100, Math.max(prev, target, basePct));
      });
    }, 100);
    return () => window.clearInterval(id);
  }, [isSubmitting, uploadProgress]);

  const handleFileSelect = (selectedFiles: FileList | null) => {
    if (!selectedFiles) return;
    const newFiles: UploadedFile[] = [];
    Array.from(selectedFiles).forEach((file) => {
      const id = crypto.randomUUID();
      const uf: UploadedFile = { id, file };
      if (file.type.startsWith("image/")) uf.preview = URL.createObjectURL(file);
      newFiles.push(uf);
    });
    setFiles((prev) => [...prev, ...newFiles]);
  };

  const removeFile = (id: string) => {
    setFiles((prev) => {
      const file = prev.find((f) => f.id === id);
      if (file?.preview) URL.revokeObjectURL(file.preview);
      return prev.filter((f) => f.id !== id);
    });
  };

  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = (e: React.DragEvent) => { e.preventDefault(); setIsDragging(false); };
  const handleDrop = (e: React.DragEvent) => { e.preventDefault(); setIsDragging(false); handleFileSelect(e.dataTransfer.files); };
  const getFileIcon = (file: File) => file.type.startsWith("image/") ? <Image className="h-4 w-4" /> : <FileText className="h-4 w-4" />;

  const handleDescriptionSubmit = useCallback(() => {
    if (!message.trim() && files.length === 0) { toast.error("Please describe your need or upload a reference."); return; }
    setStep("questions");
    setCurrentQ(0);
  }, [message, files]);

  const handleAnswer = useCallback((value: string) => {
    const key = questions[currentQ].key;
    if (value === "Other") {
      setOtherTexts((prev) => ({ ...prev, [key]: prev[key] ?? "" }));
      const customText = otherTexts[key] || "";
      setAnswers((prev) => ({ ...prev, [key]: customText ? `Other: ${customText}` : "Other:" }));
    } else {
      setOtherTexts((prev) => { const n = { ...prev }; delete n[key]; return n; });
      setAnswers((prev) => ({ ...prev, [key]: value }));
    }
  }, [currentQ, otherTexts]);

  const handleOtherTextChange = useCallback((value: string) => {
    const key = questions[currentQ].key;
    setOtherTexts((prev) => ({ ...prev, [key]: value }));
    setAnswers((prev) => ({ ...prev, [key]: value ? `Other: ${value}` : "" }));
  }, [currentQ]);

  const nextQuestion = useCallback(() => {
    if (currentQ < questions.length - 1) setCurrentQ((p) => p + 1);
    else setStep("summary");
  }, [currentQ]);

  const prevQuestion = useCallback(() => {
    if (currentQ > 0) setCurrentQ((p) => p - 1);
    else setStep("input");
  }, [currentQ]);

  const handleSubmitRequest = useCallback(async () => {
    setIsSubmitting(true);
    setUploadProgress(files.length > 0 ? { current: 0, total: files.length } : null);
    setDisplayUploadPercent(0);

    try {
      let completedUploads = 0;
      const uploadPromises = files.map(async (uploadedFile) => {
        let fileToUpload: File = uploadedFile.file;
        if (uploadedFile.file.type.startsWith("image/")) {
          try {
            const compressedBlob = await imageCompression(uploadedFile.file, IMAGE_COMPRESSION_OPTIONS);
            const compressedName = uploadedFile.file.name.replace(/\.[^/.]+$/, ".jpg");
            fileToUpload = new File([compressedBlob], compressedName, { type: "image/jpeg" });
          } catch { /* fallback */ }
        }
        const fileExt = fileToUpload.name.split('.').pop();
        const fileName = `${crypto.randomUUID()}.${fileExt}`;
        const filePath = `submissions/${fileName}`;
        const { error: uploadError } = await supabase.storage.from('conversation-files').upload(filePath, fileToUpload);
        if (uploadError) throw new Error(`Failed to upload ${uploadedFile.file.name}`);
        completedUploads++;
        setUploadProgress({ current: completedUploads, total: files.length });
        return filePath;
      });

      const fileUrls = await Promise.all(uploadPromises);
      const submissionId = crypto.randomUUID();

      const { error: dbError } = await supabase.from('conversation_submissions').insert({
        id: submissionId,
        message: [message.trim(), `Space: ${answers.space_type}`, `Location: ${answers.project_location}`].filter(Boolean).join('\n'),
        file_urls: fileUrls
      });
      if (dbError) throw new Error('Failed to save submission');

      const { data: conciergeData } = await supabase.from('concierge_requests').insert({
        initial_description: message.trim(),
        space_type: answers.space_type,
        project_location: answers.project_location,
        summary: { project: message, space_type: answers.space_type, location: answers.project_location },
        confirmed: false,
        status: "new"
      }).select('id').single();
      const conciergeId = conciergeData?.id || null;
      setPendingConciergeId(conciergeId);

      setPendingSubmissionId(submissionId);

      // Check if user is already logged in
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        // User is logged in — save email from their account and submit
        const userEmail = user.email || "";
        setEmail(userEmail);
        await supabase.from('conversation_submissions').update({ email: userEmail }).eq('id', submissionId);
        try {
          await supabase.functions.invoke('notify-submission', {
            body: { submissionId, notifyEmail: NOTIFY_EMAIL }
          });
        } catch { /* ignore */ }
        await supabase.from("projects").insert({
          user_id: user.id,
          name: message.trim().slice(0, 100) || "Untitled Project",
          description: [`Space: ${answers.space_type}`, `Location: ${answers.project_location}`].filter(Boolean).join(' — '),
          status: "draft",
          materials_count: 0,
          concierge_request_id: conciergeId,
        });
        setProjectCreated(true);
        toast.success("Thank you. We will be in touch shortly.");
        setStep("submitted");
      } else {
        // Not logged in — show auth modal which doubles as email collection
        setShowAuthModal(true);
        setStep("submitted");
      }
    } catch (error) {
      logger.error('Submission error:', error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
      setUploadProgress(null);
    }
  }, [message, answers, files]);


  const resetAll = useCallback(() => {
    setMessage("");
    files.forEach((f) => { if (f.preview) URL.revokeObjectURL(f.preview); });
    setFiles([]);
    setEmail("");
    setStep("input");
    setCurrentQ(0);
    setAnswers({ space_type: "", project_location: "" });
    setPendingSubmissionId(null);
    setProjectCreated(false);
  }, [files]);

  // Style classes based on variant
  const textColor = isHero ? "text-white" : "text-foreground";
  const mutedColor = isHero ? "text-white/60" : "text-muted-foreground";
  const borderColor = isHero ? "border-white/20" : "border-border/50";
  const hoverBorder = isHero ? "hover:border-white/40" : "hover:border-foreground/30";
  const activeBorder = isHero ? "border-white/40 bg-white/10" : "border-foreground/40 bg-foreground/[0.03]";
  const btnClass = isHero
    ? `h-12 border-white/20 bg-white/10 backdrop-blur-sm px-10 font-sans text-sm font-light tracking-[0.15em] text-white transition-all hover:border-white/40 hover:bg-white/20`
    : `h-12 border-foreground/20 bg-transparent px-10 font-sans text-sm font-light tracking-[0.15em] text-foreground transition-all hover:border-foreground/40 hover:bg-foreground/5`;
  const backBtnClass = isHero
    ? `h-12 px-6 font-sans text-sm font-light tracking-[0.15em] text-white/60 hover:bg-transparent hover:text-white`
    : `h-12 px-6 font-sans text-sm font-light tracking-[0.15em] text-muted-foreground hover:bg-transparent hover:text-foreground`;

  return (
    <>
      <div className="relative min-h-[380px]">
        <AnimatePresence mode="wait">
          {/* ── Input ── */}
          {step === "input" && (
            <motion.div
              key="input"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.4, ease }}
              className="space-y-6"
            >
              <div
                className={`group/block relative rounded-xl border ${isHero ? "border-white/20 bg-white/[0.08] backdrop-blur-xl" : "border-border bg-card/60"} shadow-lg transition-all duration-500 ease-out hover:shadow-[0_8px_40px_-8px_hsl(var(--primary)/0.25)] hover:-translate-y-1 hover:scale-[1.01] focus-within:ring-2 ${isHero ? "focus-within:ring-white/30 focus-within:border-white/40 hover:border-white/40 hover:bg-white/[0.14] hover:shadow-[0_8px_40px_-8px_rgba(255,255,255,0.2)]" : "focus-within:ring-ring/30 focus-within:border-foreground/40 hover:border-primary/30 hover:bg-card/80"}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <textarea
                  ref={(el) => {
                    if (el) {
                      el.style.height = 'auto';
                      el.style.height = Math.max(48, Math.min(el.scrollHeight, 200)) + 'px';
                    }
                  }}
                  value={message}
                  onChange={(e) => {
                    setMessage(e.target.value);
                    const el = e.target;
                    el.style.height = 'auto';
                    el.style.height = Math.max(48, Math.min(el.scrollHeight, 200)) + 'px';
                  }}
                  rows={1}
                  aria-label="Describe your project"
                  className={`min-h-[92px] w-full resize-none bg-transparent px-5 pt-4 pb-2 font-serif text-lg leading-relaxed ${isHero ? "text-white placeholder:text-white/40" : "text-foreground"} focus:outline-none transition-all sm:min-h-[56px] sm:text-xl md:text-2xl`}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey && message.trim()) {
                      e.preventDefault();
                      handleDescriptionSubmit();
                    }
                  }}
                />
                {!message && (
                  <div className={`pointer-events-none absolute left-5 right-5 top-4 max-w-[calc(100%-2.5rem)] whitespace-normal break-words pt-px font-serif text-lg leading-relaxed ${isHero ? "text-white/40" : "text-muted-foreground/50"} sm:text-xl md:text-2xl`}>
                    {placeholder}
                    <span className={`ml-0.5 inline-block w-[2px] h-[1.1em] align-middle ${isHero ? "bg-white/70" : "bg-primary/70"} animate-[blink_1s_step-end_infinite]`} />
                  </div>
                )}

                {/* Uploaded files */}
                {files.length > 0 && (
                  <div className="flex flex-wrap gap-2 px-4 pb-2">
                    {files.map((uf) => (
                      <div key={uf.id} className={`flex items-center gap-2 border ${borderColor} ${isHero ? "bg-white/10" : "bg-muted/20"} px-2.5 py-1.5 rounded text-xs`}>
                        {uf.preview ? <img src={uf.preview} alt={uf.file.name} className="h-6 w-6 object-cover rounded" /> : getFileIcon(uf.file)}
                        <span className={`max-w-[100px] truncate font-sans font-light ${mutedColor}`}>{uf.file.name}</span>
                        <button onClick={(e) => { e.stopPropagation(); removeFile(uf.id); }} className={`${mutedColor} transition-colors hover:opacity-80`}>
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Bottom bar: upload + continue */}
                <div className={`flex items-center justify-between border-t ${isHero ? "border-white/10" : "border-border/30"} px-4 py-3`}>
                  <input ref={fileInputRef} type="file" multiple accept="image/*,.pdf,.doc,.docx" onChange={(e) => handleFileSelect(e.target.files)} className="hidden" />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className={`flex items-center gap-2 font-sans text-xs font-light tracking-wide ${mutedColor} transition-colors hover:opacity-80`}
                  >
                    <Upload className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">Attach file</span>
                  </button>
                  <Button
                    onClick={handleDescriptionSubmit}
                    disabled={!message.trim() && files.length === 0}
                    size="sm"
                    className={`${isHero ? "bg-white/20 text-white hover:bg-white/30 border-white/20" : "bg-foreground/10 text-foreground hover:bg-foreground/20 border-foreground/20"} border font-sans text-xs font-light tracking-[0.1em] px-5 py-2 h-auto rounded`}
                  >
                    Continue <ArrowRight className="ml-1.5 h-3 w-3" />
                  </Button>
                </div>
              </div>
            </motion.div>
          )}

          {/* ── Questions ── */}
          {step === "questions" && (
            <motion.div
              key={`q-${currentQ}`}
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -24 }}
              transition={{ duration: 0.35, ease }}
              className="space-y-8"
            >
              <div className="flex items-center gap-2">
                {questions.map((_, i) => (
                  <div key={i} className={`h-px flex-1 transition-colors duration-500 ${i <= currentQ ? (isHero ? "bg-white" : "bg-foreground") : (isHero ? "bg-white/20" : "bg-border/40")}`} />
                ))}
                <span className={`ml-2 font-sans text-[11px] tabular-nums ${mutedColor}`}>{currentQ + 1}/{questions.length}</span>
              </div>

              <h3 className={`font-serif text-xl font-normal leading-relaxed ${textColor} tracking-wide md:text-2xl`}>
                {questions[currentQ].label}
              </h3>

              {(() => {
                const key = questions[currentQ].key;
                const val = answers[key];
                const opts = questions[currentQ].options;
                const isOtherSelected = opts.includes("Other") && (val?.startsWith("Other:") || otherTexts[key] !== undefined);
                const radioVal = isOtherSelected ? "Other" : val;
                return (
                  <>
                    <RadioGroup value={radioVal} onValueChange={handleAnswer} className="grid gap-3 sm:grid-cols-2">
                      {opts.map((opt) => (
                        <label
                          key={opt}
                          className={`flex cursor-pointer items-center gap-3 border px-4 py-3 font-sans text-sm font-light transition-all duration-200 rounded ${
                            (opt === "Other" ? isOtherSelected : val === opt)
                              ? activeBorder + " " + textColor
                              : `${borderColor} ${mutedColor} ${hoverBorder} hover:${textColor}`
                          }`}
                        >
                          <RadioGroupItem value={opt} className="shrink-0" />
                          {optionIcons[opt]}
                          {opt}
                        </label>
                      ))}
                    </RadioGroup>
                    {isOtherSelected && (
                      <Input
                        autoFocus
                        placeholder="Please specify…"
                        value={otherTexts[key] || ""}
                        onChange={(e) => handleOtherTextChange(e.target.value)}
                        className={`mt-2 ${isHero ? "border-white/20 bg-white/10 backdrop-blur-sm text-white placeholder:text-white/40" : "border-foreground/20 bg-transparent"} font-sans text-sm font-light`}
                      />
                    )}
                  </>
                );
              })()}

              <div className="flex items-center justify-center gap-4 pt-2">
                <Button onClick={prevQuestion} variant="ghost" className={backBtnClass}>
                  <ArrowLeft className="mr-2 h-3.5 w-3.5" /> Back
                </Button>
                <Button onClick={nextQuestion} disabled={!answers[questions[currentQ].key]} variant="outline" className={btnClass}>
                  {currentQ < questions.length - 1 ? "Next" : "Review"} <ArrowRight className="ml-2 h-3.5 w-3.5" />
                </Button>
              </div>
            </motion.div>
          )}

          {/* ── Summary ── */}
          {step === "summary" && (
            <motion.div
              key="summary"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.4, ease }}
              className="space-y-8"
            >
              <div className="text-center">
                <h3 className={`font-serif text-xl font-normal leading-relaxed ${textColor} tracking-wide md:text-2xl`}>Your request summary</h3>
              </div>

              <dl className={`mx-auto max-w-md space-y-4 border-l ${borderColor} pl-6`}>
                {[["Project", message], ["Space", answers.space_type], ["Location", answers.project_location]].filter(([, v]) => v).map(([label, value]) => (
                  <div key={label}>
                    <dt className={`font-sans text-[11px] uppercase tracking-[0.15em] ${mutedColor}`}>{label}</dt>
                    <dd className={`mt-0.5 font-serif text-base ${textColor}`}>{value}</dd>
                  </div>
                ))}
                {files.length > 0 && (
                  <div>
                    <dt className={`font-sans text-[11px] uppercase tracking-[0.15em] ${mutedColor}`}>Attachments</dt>
                    <dd className="mt-1 flex flex-wrap gap-2">
                      {files.map((f) => (
                        <span key={f.id} className={`border ${borderColor} px-2 py-1 font-sans text-xs font-light ${mutedColor}`}>{f.file.name}</span>
                      ))}
                    </dd>
                  </div>
                )}
              </dl>

              {isSubmitting && uploadProgress && (
                <div className="mx-auto max-w-xs space-y-2">
                  <Progress value={displayUploadPercent} className="h-1 bg-border/30" />
                  <p className={`text-center font-sans text-xs font-light tracking-wide ${mutedColor}`}>
                    Uploading {uploadProgress.current}/{uploadProgress.total} · {Math.round(displayUploadPercent)}%
                  </p>
                </div>
              )}

              <div className="flex items-center justify-center gap-4 pt-2">
                <Button onClick={() => { setStep("questions"); setCurrentQ(0); }} disabled={isSubmitting} variant="ghost" className={backBtnClass}>
                  <Edit2 className="mr-2 h-3.5 w-3.5" /> Edit
                </Button>
                <Button onClick={handleSubmitRequest} disabled={isSubmitting} variant="outline" className={btnClass}>
                  {isSubmitting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{uploadProgress ? "Uploading…" : "Saving…"}</> : <>Submit <Send className="ml-2 h-3.5 w-3.5" /></>}
                </Button>
              </div>
            </motion.div>
          )}
          {/* ── Submitted ── */}
          {step === "submitted" && (
            <motion.div
              key="submitted"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease }}
              className="flex flex-col items-center gap-6 py-12 text-center"
            >
              <div className={`flex h-14 w-14 items-center justify-center border ${borderColor}`}>
                <Check className={`h-6 w-6 ${textColor}`} />
              </div>
              <div>
                <h3 className={`font-serif text-xl font-normal leading-relaxed ${textColor} tracking-wide md:text-2xl`}>Request Submitted</h3>
                <p className={`mt-4 mx-auto max-w-sm font-sans text-base font-light leading-relaxed ${mutedColor} tracking-wide`}>
                  Our team will review the details and get back to you with tailored recommendations.
                </p>
              </div>
              {projectCreated ? (
                <div className="space-y-3">
                  <p className={`font-sans text-sm font-light ${mutedColor}`}>A new project has been added to your dashboard.</p>
                  <a href="/projects" className={`inline-block font-sans text-sm font-light tracking-[0.15em] ${textColor} transition-colors hover:opacity-70`}>View My Projects →</a>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className={`font-sans text-sm font-light ${mutedColor}`}>Sign in to save this as a project and track progress.</p>
                  <button
                    onClick={() => setShowAuthModal(true)}
                    className={`inline-flex items-center gap-2 border ${borderColor} px-6 py-3 font-sans text-sm font-light tracking-[0.15em] ${textColor} transition-all ${hoverBorder} ${isHero ? "hover:bg-white/10" : "hover:bg-foreground/5"}`}
                  >
                    <LogIn className="h-3.5 w-3.5" /> Sign in to save project
                  </button>
                </div>
              )}
              <button onClick={resetAll} className={`font-sans text-sm font-light tracking-[0.15em] ${mutedColor} transition-colors hover:opacity-80`}>
                Submit another request →
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <AuthModal
        open={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        defaultEmail={email}
        contextHeadline="Where should we send our response?"
        contextSubtext="Sign in or create an account so we can follow up with tailored recommendations."
        onSuccess={async () => {
          const { data: { user } } = await supabase.auth.getUser();
          if (user) {
            // Update submission with user's email
            if (pendingSubmissionId) {
              const userEmail = user.email || "";
              await supabase.from('conversation_submissions').update({ email: userEmail }).eq('id', pendingSubmissionId);
              try {
                await supabase.functions.invoke('notify-submission', {
                  body: { submissionId: pendingSubmissionId, notifyEmail: NOTIFY_EMAIL }
                });
              } catch { /* ignore */ }
            }
            await supabase.from("projects").insert({
              user_id: user.id,
              name: message.trim().slice(0, 100) || "Untitled Project",
              description: [`Space: ${answers.space_type}`, `Location: ${answers.project_location}`].filter(Boolean).join(' — '),
              status: "draft",
              materials_count: 0,
              concierge_request_id: pendingConciergeId,
            });
            setProjectCreated(true);
            toast.success("Thank you. We will be in touch shortly.");
          }
        }}
      />
    </>
  );
}
